import { Injectable } from '@angular/core';
import {
  Menu,
  AbstractMenuItem,
  MenuItem,
  SeparatorItem,
  ObjectMenu,
} from 'src/app/_model/menu';
import { LoggingService } from '../../../logging/logic/logging.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { MenuService } from '../menu.service';
import { BehaviorSubject } from 'rxjs';
import { ClientStateService } from '../../../client-state/logic/client-state.service';
import { ClientState, SystemState } from 'src/app/_model/client-state';
import { MenuDaoService } from '../../dao/menu-dao.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Interface Definition for a Menu Service
 *
 * MenuService loads the Menu always from a remote server and as a fallback it will use the latest saved menu
 */
@Injectable()
export class RemoteMenuService implements MenuService {
  // Public Member
  public currentMenu$ = new BehaviorSubject<Menu>(undefined);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Private Member
  private allStates = [
    SystemState.NONE,
    SystemState.PAUSESTARTED,
    SystemState.SERVICEJOBSTARTED,
    SystemState.TRAVEL,
    SystemState.WAITSTARTED,
    SystemState.WORKSTARTED,
  ];

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private menuDaoService: MenuDaoService,
    private authenticationService: AuthenticationService,
  ) {
    this.initialize();
  }

  public findDeficiencyObjectMenu(): ObjectMenu {
    return this.currentMenu$
      .getValue()
      .mainMenu.items.find(
        (it) =>
          it.kind === 'objectmenu' &&
          (it as ObjectMenu).objecttype == 'ims/DeficiencyTicket',
      ) as ObjectMenu;
  }

  // Resetable
  public async reset() {
    await this.menuDaoService.reset();
  }

  public async clear() {
    await this.menuDaoService.clear();
    this.currentMenu$.next(undefined);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteMenu(apiLevel, syncKey);
  }
  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalMenu();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalMenu() {
    try {
      this.initializeStatus$.next(InitializeStatus.INITIALIZING);
      this.logger.log(this.constructor.name, '- Load local Menu');
      let menu: Menu = await this.menuDaoService.loadMenu();

      this.setMenu(menu);
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Could not load local Menu:',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
      throw error;
    }
  }

  private async loadRemoteMenu(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(this.constructor.name, '- Load remote Menus from IMS');

      try {
        let menu: Menu = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'menudefinition',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          }),
        );

        this.logger.log(this.constructor.name, 'Downloaded Menu: ', menu);

        await this.menuDaoService.saveMenu(menu);
        this.setMenu(menu);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          '- Could not download MenuDefinition',
          error?.message,
        );
        throw error;
      }
    } else {
      let error = new Error('Cannot download Menus - user not authenticated');
      this.logger.error(this.constructor.name, error?.message);
      throw error;
    }
  }

  private setMenu(menu: Menu) {
    if (menu) {
      this.logger.log(
        this.constructor.name,
        '- Finished loading MenuDefinition with respones',
        menu,
      );

      const resetItem: MenuItem = {
        kind: 'menuitem',
        visibility: {
          rule: 'always',
        },
        label: {
          text: 'ITEMS.RESET',
        },
        icon: 'construct',
        action: {
          type: 'action',
          target: 'reset',
        },
        longpressAction: {
          type: 'action',
          target: 'hardreset',
          params: {
            color: 'danger',
            icon: 'trash-bin',
            label: 'ITEMS.HARDRESET',
          },
        },
      };
      const syncItem: MenuItem = {
        kind: 'menuitem',
        visibility: {
          rule: 'always',
        },
        label: {
          text: 'ITEMS.SYNC',
        },
        icon: 'sync',
        action: {
          type: 'action',
          target: 'sync',
          params: {
            silent: 'false',
          },
        },
      };
      const contactItem: MenuItem = {
        kind: 'menuitem',
        visibility: {
          rule: 'always',
        },
        label: {
          text: 'ITEMS.LBSINFO',
        },
        icon: 'information-circle',
        action: {
          type: 'navigation',
          target: 'lbs-info',
        },
      };
      const logoutItem: MenuItem = {
        kind: 'menuitem',
        visibility: {
          rule: 'always',
        },
        label: {
          text: 'ITEMS.LOGOUT',
        },
        icon: 'log-out',
        action: { type: 'action', target: 'logout' },
      };

      const peripheralsItem: MenuItem = {
        kind: 'menuitem',
        visibility: {
          rule: 'hasPeripherals',
        },
        label: {
          text: 'ITEMS.PERIPHERALS',
        },
        icon: 'bluetooth',
        action: {
          type: 'navigation',
          target: 'peripherals',
        },
      };

      // JB20200328: Needed to check for undefined as upgrade to
      // Menüdefinition V2 lets this property access fail with undefined.
      // Old menu in the storage cannot be mapped anymore to the new
      // menu definition which leaves the menu undefined
      if (menu && menu.settingsMenu && menu.settingsMenu.items) {
        menu.settingsMenu.items.push(resetItem);
        menu.settingsMenu.items.push(syncItem);
        menu.settingsMenu.items.push(peripheralsItem);
        menu.settingsMenu.items.push(contactItem);
        menu.settingsMenu.items.push(logoutItem);

        for (const item of menu?.settingsMenu?.items) {
          if (item.kind === 'menuitem') {
            const i = item as MenuItem;
            if (i?.action?.target === 'send_log2') {
              if (i.action.params == undefined) {
                i.action.params = {};
              }
              if (i.action.params.silent == undefined) {
                i.action.params.silent = 'false';
              }
            }
          }
        }
      }

      if (menu.extraMenues == undefined) {
        menu.extraMenues = [];
      }

      menu.extraMenues.push({
        type: 'messages',
        icon: 'mail',
        label: 'TABS.SINGLEPAGE.MESSAGES',
        labelTranslate: true,
        badge: {
          color: 'danger',
          type: 'dynamic',
          value: 'unreadmessages',
        },
        items: [],
        toolbars: menu?.mainMenu?.toolbars,
      });

      this.currentMenu$.next(menu);
    }
  }

  async initializeCompleted(): Promise<void> {}
}
